export const angleMeasurementDegrees = (points) => {
  if (!Array.isArray(points) || points.length < 3) return null;
  const [a, b, c] = points;
  const first = { x: a.x - b.x, y: a.y - b.y };
  const second = { x: c.x - b.x, y: c.y - b.y };
  const denominator = Math.hypot(first.x, first.y) * Math.hypot(second.x, second.y);
  if (!denominator) return null;
  const cosine = Math.max(-1, Math.min(1, (first.x * second.x + first.y * second.y) / denominator));
  return Math.acos(cosine) * 180 / Math.PI;
};

export const formatAngleMeasurementLabel = (annotation) => {
  const degrees = angleMeasurementDegrees(annotation?.points);
  return degrees == null ? '' : `${degrees.toFixed(1)}°`;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const pillMetrics = (label, fontSize) => {
  const paddingX = 5;
  const paddingY = 3;
  return {
    paddingX,
    paddingY,
    boxWidth: Math.max(fontSize, label.length * fontSize * 0.62) + paddingX * 2,
    boxHeight: fontSize + paddingY * 2,
  };
};

const pillLayoutAt = ({ center, label, fontSize, viewportWidth, viewportHeight }) => {
  const { paddingX, paddingY, boxWidth, boxHeight } = pillMetrics(label, fontSize);
  const boxX = clamp(center.x - boxWidth / 2, 4, Math.max(4, viewportWidth - boxWidth - 4));
  const boxY = clamp(center.y - boxHeight / 2, 4, Math.max(4, viewportHeight - boxHeight - 4));
  return {
    boxX,
    boxY,
    boxWidth,
    boxHeight,
    textX: boxX + paddingX,
    textY: boxY + paddingY + fontSize,
  };
};

const candidateOverflow = ({ center, boxWidth, boxHeight, viewportWidth, viewportHeight }) => {
  const rawX = center.x - boxWidth / 2;
  const rawY = center.y - boxHeight / 2;
  return Math.max(0, 4 - rawX)
    + Math.max(0, rawX + boxWidth - (viewportWidth - 4))
    + Math.max(0, 4 - rawY)
    + Math.max(0, rawY + boxHeight - (viewportHeight - 4));
};

const choosePillCenter = ({ origin, directions, clearance, label, fontSize, viewportWidth, viewportHeight }) => {
  const { boxWidth, boxHeight } = pillMetrics(label, fontSize);
  return directions
    .map((direction, priority) => {
      const center = {
        x: origin.x + direction.x * clearance,
        y: origin.y + direction.y * clearance,
      };
      return {
        center,
        priority,
        overflow: candidateOverflow({ center, boxWidth, boxHeight, viewportWidth, viewportHeight }),
      };
    })
    .sort((left, right) => left.overflow - right.overflow || left.priority - right.priority)[0].center;
};

export const linearMeasurementLabelLayout = ({
  a,
  b,
  label,
  fontSize,
  viewportWidth,
  viewportHeight,
}) => {
  const midpoint = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  const normal = length > 0.001 ? { x: -dy / length, y: dx / length } : { x: 0, y: -1 };
  const upwardNormal = normal.y <= 0 ? normal : { x: -normal.x, y: -normal.y };
  const center = choosePillCenter({
    origin: midpoint,
    directions: [upwardNormal, { x: -upwardNormal.x, y: -upwardNormal.y }],
    clearance: fontSize + 10,
    label,
    fontSize,
    viewportWidth,
    viewportHeight,
  });
  return pillLayoutAt({ center, label, fontSize, viewportWidth, viewportHeight });
};

export const angleMeasurementLabelLayout = ({
  points,
  label,
  fontSize,
  viewportWidth,
  viewportHeight,
}) => {
  const [first, vertex, third] = points;
  const firstLength = Math.hypot(first.x - vertex.x, first.y - vertex.y) || 1;
  const thirdLength = Math.hypot(third.x - vertex.x, third.y - vertex.y) || 1;
  const interior = {
    x: (first.x - vertex.x) / firstLength + (third.x - vertex.x) / thirdLength,
    y: (first.y - vertex.y) / firstLength + (third.y - vertex.y) / thirdLength,
  };
  const interiorLength = Math.hypot(interior.x, interior.y);
  const chordLength = Math.hypot(third.x - first.x, third.y - first.y) || 1;
  const outward = interiorLength > 0.001
    ? { x: -interior.x / interiorLength, y: -interior.y / interiorLength }
    : { x: -(third.y - first.y) / chordLength, y: (third.x - first.x) / chordLength };
  const { boxWidth, boxHeight } = pillMetrics(label, fontSize);
  const center = choosePillCenter({
    origin: vertex,
    directions: [outward, { x: -outward.x, y: -outward.y }],
    clearance: Math.max(boxWidth, boxHeight) / 2 + fontSize,
    label,
    fontSize,
    viewportWidth,
    viewportHeight,
  });
  return pillLayoutAt({ center, label, fontSize, viewportWidth, viewportHeight });
};
